/**
 * Word Labyrinth — reading a sentence pack into board-ready shape (specs/labyrinth-mode.md §6).
 *
 * The mode costs no new content in any language because both halves of a board are already
 * authored in `src/data/packs/sentences.<lang>.json`, the Sentence Order data: the path words are
 * the set's own tiles, and the wrong words are its own `variantOf` decoys.
 *
 * The one thing worth stating loudly, because it is what makes this a ten-language mode rather
 * than a seven-language one:
 *
 * > **A path cell is a `folge` tile, never a whitespace token.** Thai writes `หมาเห่าดัง` with no
 * > spaces at all. Its set stores `หมา` + `เห่า` + `ดัง` as separate words because Sentence Order
 * > needed them separated, so the segmentation problem this mode would otherwise have to solve at
 * > runtime — in a WebView with no Thai dictionary — was solved by the content years ago.
 *
 * Phaser-free, like the rest of `game/labyrinth/`.
 */
import type { Pack, PackWord } from '@/data/packs/types';

export interface SentenceSet {
  id: string;
  /** Full surface form, for logging and tests. */
  text: string;
  /** The pre-filled opening, or `''` when the set is a bare noun phrase. ONE cell when present. */
  vorgabe: string;
  /** The `folge` tiles in order, without the vorgabe. */
  tiles: string[];
  /** The cells a path made of this set carries, in reading order: vorgabe (if any) then tiles. */
  cells: string[];
  /** Authored wrong-form decoys — `variantOf` words, safe by §6.2's inherited rule. */
  decoys: string[];
}

const str = (w: PackWord, field: string): string =>
  typeof w[field] === 'string' ? (w[field] as string) : '';

/**
 * Every usable sentence of a pack.
 *
 * A set is one sentence: exactly one word carries `folge` (the tile ids in order) and is the head;
 * the rest are its tiles and its decoys. Sets whose head is malformed are dropped rather than
 * repaired — a half-read sentence would put a wrong word on the true path, which is the one bug
 * this mode cannot survive.
 */
export function readSentenceSets(pack: Pack): SentenceSet[] {
  const out: SentenceSet[] = [];
  for (const set of pack.sets ?? []) {
    const head = set.words.find((w) => str(w, 'folge') !== '');
    if (!head) continue;

    const byId = new Map<string, PackWord>();
    for (const w of set.words) if (w.id) byId.set(w.id, w);

    const ids = str(head, 'folge').split(',').map((s) => s.trim()).filter(Boolean);
    const tiles = ids.map((id) => byId.get(id)?.text ?? '');
    if (tiles.some((t) => t === '')) continue;

    const vorgabe = str(head, 'vorgabe');
    const decoys = Array.from(
      new Set(set.words.filter((w) => str(w, 'variantOf') !== '').map((w) => w.text)),
    );

    out.push({
      id: set.id,
      text: head.text,
      vorgabe,
      tiles,
      cells: vorgabe ? [vorgabe, ...tiles] : tiles,
      decoys,
    });
  }
  return out;
}

/**
 * The sets a board may use (§6.5).
 *
 * `maxWordChars` is applied to the decoys as well as the path words, because a decoy is a cell like
 * any other — a 15-character wrong word falls under `minFontPx` at the round-5 cell width just as
 * surely as a right one does.
 */
export function eligibleSets(
  sets: SentenceSet[],
  maxWordChars: number,
  minDistinctDecoys: number,
): SentenceSet[] {
  return sets.filter((s) => {
    if (s.decoys.length < minDistinctDecoys) return false;
    return [...s.cells, ...s.decoys].every((w) => [...w].length <= maxWordChars);
  });
}
